import type {
  NodeVariable,
  Variable,
} from "@/workflow/components/configurations/types";
import { getForm } from "@/workflow_api/services/nodes.api";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import axios from "@/utils/axios";
import { useEffect, useState, useMemo } from "react";
import type { Node } from "@xyflow/react";
import type { VariableType } from "@/workflow/components/configurations/calculated-field/CalculatedFieldOperation/types";
import { Operations } from "@/workflow/components/configurations/calculated-field/CalculatedFieldOperation/utils/operations";
import type { Operation } from "@/workflow/components/configurations/if/operations";
import type { DataCenterSource } from "@/workflow/components/configurations/data-center/types";
import {
  getNodeActionString,
  getDataCenterOutputName,
  getTableSource,
} from "@/workflow/components/configurations/data-center/serialization/deserialization";
import { getOperationVerb } from "@/utils/format";
import { deserializeInteroperabilityNode } from "@/workflow/components/configurations/interoperability/serialization/deserialization";
import { getN8NFlowSchema } from "@/queries/n8n";
import { getTableDetailsByTableName } from "@/queries/data-center/data-center.api";
// import { useGetTableData } from "@/queries/data-center/data-center.api";

const createExecutionStartVariable = (nodeId: string): Variable => {
  return {
    id: `${nodeId}_execution_start`,
    name: "inicio-nodo",
    type: "date" as VariableType,
    node_id: nodeId,
    global_type: "NODE_EXECUTION_START" as const,
  };
};

const createExecutionEndVariable = (nodeId: string): Variable => {
  return {
    id: `${nodeId}_execution_finish`,
    name: "fin-nodo",
    type: "date" as VariableType,
    node_id: nodeId,
    global_type: "NODE_EXECUTION_FINISH" as const,
  };
};

interface FormData {
  steps: Array<{
    name: string;
    sections: Array<{
      fields: Array<{
        id: string;
        name: string;
        label: string;
        type: string;
        config?: Record<string, string | boolean | number | undefined>;
      }>;
    }>;
  }>;
}

type DataCenterTableColumnsResponse =
  | {
      columns?: Array<{ name: string; type: string }>;
    }
  | Array<{ name: string; type: string }>;

interface ExpedientBaseEntityField {
  id: string | number;
  name: string;
  field_type: string;
  configuration?: {
    key?: string | null;
  };
}

interface GeneratedDocumentInput {
  id?: string;
  name?: string;
}

interface GeneratedDocument {
  inputs?: GeneratedDocumentInput[];
}

interface InteroperabilityResponseField {
  type?: string;
  display_name?: string;
}

type InteroperabilityResponseData = Record<
  string,
  InteroperabilityResponseField
>;

type N8NOutputs = Array<Record<string, { type: string }>>;

export const parseType = (
  type: string,
  multiple = false,
  selectType?: string,
): "string" | "number" | "boolean" | "date" | "array" | "file" | "select" => {
  // console.log(type);
  if (multiple) {
    return "array";
  }
  switch (type.toLowerCase()) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "dui_nit": {
      return "string";
    }
    case "date":
    case "datetime":
    case "date_range":
    case "timestamp":
    case "datetime_range": {
      return "date";
    }
    case "float":
    case "integer": {
      return "number";
    }
    case "select": {
      if (selectType === "boolean") {
        return "boolean";
      }
      if (selectType === "number") {
        return "number";
      }
      return "string";
    }
    case "switch": {
      return "boolean";
    }
    default:
      return type.toLowerCase() as
        | "string"
        | "number"
        | "boolean"
        | "date"
        | "array";
  }
};

export const useGetVariables = (overrideNodeIds?: string | string[]) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [variables, setVariables] = useState<NodeVariable[]>([]);
  const { selectedNodeId: storeNodeId } = useUIStore();
  const { getNodesVariabes, nodes: allNodes } = useWorkflowStore();

  const selectedNodeId = overrideNodeIds || storeNodeId;

  const memoizedNodes = useMemo(() => {
    if (!selectedNodeId) return [];

    const allMatched = new Map<string, Node>();
    const nodeIds = Array.isArray(selectedNodeId)
      ? selectedNodeId
      : [selectedNodeId];

    nodeIds.forEach((id) => {
      const node = allNodes.find((n) => n.id === id);
      if (node) allMatched.set(node.id, node);

      getNodesVariabes(id).forEach((n: Node) => allMatched.set(n.id, n));
    });

    return Array.from(allMatched.values());
  }, [selectedNodeId, getNodesVariabes, allNodes]);

  useEffect(() => {
    if (memoizedNodes.length === 0) {
      setIsLoading(false);
      setVariables([]);
      return;
    }
    const nodes = memoizedNodes;
    setIsLoading(true);
    const newVariables: NodeVariable[] = [];
    const notEmptyNodes = nodes.filter((n: Node) => {
      const data = n.data as unknown as NodeData;
      return data.type === NodeTypes.INITIALIZER || data.inputs.length > 0;
    });
    const fetchNodeVariables = async (
      n: Node,
      idOverride?: string,
      nameOverride?: string,
    ): Promise<NodeVariable | null> => {
      const data = n.data as unknown as NodeData;
      const targetId = idOverride || n.id;
      const targetName = nameOverride || data.displayName;

      if (data.type === NodeTypes.INITIALIZER) {
        return {
          name: targetName,
          nodeId: targetId,
          type: data.type,
          ref_name: data.type,
          variables: [
            createExecutionStartVariable(targetId),
            createExecutionEndVariable(targetId),
          ],
        };
      }

      if (data.type === NodeTypes.FORM) {
        try {
          const response = await getForm(data.inputs[0].value as string);
          const formData = response.data as FormData;

          const rawVariables = await Promise.all(
            formData.steps.flatMap((step) =>
              step.sections.flatMap((section) =>
                section.fields.map(async (field) => {
                  if (
                    !field?.config?.multiple &&
                    field?.config?.options_type === "database"
                  ) {
                    const tableData = await getTableDetailsByTableName(
                      field.config?.data_center_table as string,
                      field.config?.source as DataCenterSource,
                    );

                    if (!tableData) return [];

                    const data =
                      tableData.data as unknown as DataCenterTableColumnsResponse;
                    const columns =
                      (!Array.isArray(data) ? data.columns : undefined) ||
                      (Array.isArray(data) ? data : []);

                    const hasIdColumn = columns.some(
                      (col) => col.name === "id",
                    );

                    const mapped = columns.map((col) => ({
                      id: `${field.id}-${col.name}`,
                      node_id: targetId,
                      name: `${field.name} - ${col.name}`,
                      output_name: col.name,
                      field_id: field.id,
                      type: parseType(col.type),
                    }));

                    if (!hasIdColumn) {
                      mapped.push({
                        id: `${field.id}-id`,
                        node_id: targetId,
                        name: `${field.name} - id`,
                        output_name: "id",
                        field_id: field.id,
                        type: "string",
                      });
                    }
                    return mapped;
                  }

                  return {
                    id: field.id,
                    name: field.label,
                    meta_type: field.type,
                    field_id: field.id,
                    type: parseType(
                      field.type,
                      field.config?.multiple as boolean,
                      field.config?.data_center_value_type as string,
                    ),
                    node_id: targetId,
                  };
                }),
              ),
            ),
          );

          return {
            name: targetName,
            nodeId: targetId,
            type: data.type,
            ref_name: data.type,
            variables: [
              createExecutionStartVariable(targetId),
              ...rawVariables.flat(),
              createExecutionEndVariable(targetId),
            ],
          };
        } catch (error) {
          console.error("Error fetching form variables", error);
          return null;
        }
      }

      if (data.type === NodeTypes.DATA_CENTER) {
        const action = getNodeActionString(data);
        if (action === "read") {
          const variableName = getDataCenterOutputName(data);
          if (!variableName) return null;

          return {
            name: targetName,
            nodeId: targetId,
            type: data.type,
            ref_name: data.type,
            variables: [
              createExecutionStartVariable(targetId),
              {
                id: targetId,
                name: variableName || "resultado",
                type: "array" as VariableType,
                node_id: targetId,
              },
              createExecutionEndVariable(targetId),
            ],
          };
        } else if (action) {
          return {
            name: targetName,
            nodeId: targetId,
            type: data.type,
            ref_name: data.type,
            variables: [
              createExecutionStartVariable(targetId),
              {
                id: targetId,
                name: `ids-registros-${getOperationVerb(action)}`,
                type: "array" as VariableType,
                node_id: targetId,
              },
              {
                id: targetId,
                name: `cantidad-registros-${getOperationVerb(action)}`,
                type: "number" as VariableType,
                node_id: targetId,
              },
              createExecutionEndVariable(targetId),
            ],
          };
        }
      }

      if (data.type === NodeTypes.EXPEDIENTE) {
        const operationInput = data.inputs.find((i) => i.name === "operation");
        const operation = (
          (operationInput?.value as string) || ""
        ).toLowerCase();
        const entityIdInput = data.inputs.find(
          (i) => i.name === "expedient_base_entity_id",
        );
        const entityId = entityIdInput?.value as string;

        const expedienteVariables: Variable[] = [
          createExecutionStartVariable(targetId),
          {
            id: `${targetId}-expedient_id`,
            name: "ID",
            type: "string" as VariableType,
            node_id: targetId,
            output_name: "expedient_id",
          },
          {
            id: `${targetId}-expedient_unique_code`,
            name: "Código único",
            type: "string" as VariableType,
            node_id: targetId,
            output_name: "expedient_unique_code",
          },
        ];

        if (
          ["read", "create", "update", "link_users", "unlink_users"].includes(
            operation,
          )
        ) {
          expedienteVariables.push({
            id: `${targetId}-expedient_name`,
            name: "Nombre",
            type: "string" as VariableType,
            node_id: targetId,
            output_name: "expedient_name",
          });
        }

        if (operation === "link_users") {
          expedienteVariables.push({
            id: `${targetId}-links_user_ids`,
            name: "IDs usuarios asociados",
            type: "array" as VariableType,
            node_id: targetId,
            output_name: "links_user_ids",
          });
        } else if (operation === "unlink_users") {
          expedienteVariables.push({
            id: `${targetId}-unlink_user_ids`,
            name: "IDs usuarios desasociados",
            type: "array" as VariableType,
            node_id: targetId,
            output_name: "unlink_user_ids",
          });
        }

        if (entityId && ["read", "create", "update"].includes(operation)) {
          try {
            const entityPath = `/admin/v1/expedient-base-entities/${entityId}`;
            const entityResponse = await axios.get(entityPath, {
              params: { include_fields: 1 },
            });
            const entityFields = (entityResponse.data?.data?.fields ||
              []) as ExpedientBaseEntityField[];

            entityFields.forEach((field) => {
              if (!field.id) return;
              expedienteVariables.push({
                id: `${targetId}-${field.id}`,
                name: field.name ?? "",
                type: parseType(field.field_type) as VariableType,
                node_id: targetId,
                output_name: field.configuration?.key ?? field.name ?? "",
              });
            });
          } catch (e) {
            console.error("Error fetching expediente entity fields", e);
          }
        }

        expedienteVariables.push(createExecutionEndVariable(targetId));

        return {
          name: targetName,
          nodeId: targetId,
          type: data.type,
          ref_name: data.type,
          variables: expedienteVariables,
        };
      }

      if (data.type === NodeTypes.SIGN_DOCUMENT) {
        const generatedDocs = data.inputs.find(
          (i) => i.name === "generated_documents",
        );
        const documents: Variable[] = [];

        (generatedDocs?.documents as GeneratedDocument[] | undefined)?.forEach(
          (doc) => {
            doc.inputs?.forEach((input) => {
              documents.push({
                id: input.id || "",
                name: input.name || "documento_resolutivo",
                type: "file" as VariableType,
                node_id: targetId,
                value: input.id,
              });
            });
          },
        );

        if (documents.length > 0) {
          return {
            name: targetName,
            nodeId: targetId,
            type: data.type as NodeVariable["type"],
            ref_name: data.type,
            variables: [
              createExecutionStartVariable(targetId),
              ...documents,
              createExecutionEndVariable(targetId),
            ],
          };
        }
      }

      if (data.type === NodeTypes.CALCULATED) {
        const calc = data?.inputs[0]?.calculations?.[0];
        const type = calc?.inputs?.[0]?.type;
        const operation = calc?.operation;
        const variableName = calc?.inputs?.[0]?.name;

        const frontendType = parseType(type || "");
        const outputType = frontendType
          ? Operations[frontendType as keyof typeof Operations]?.find(
              (op: Operation) => op.name === operation?.toLowerCase(),
            )?.output
          : undefined;

        return {
          name: targetName,
          nodeId: targetId,
          type: data.type,
          ref_name: data.type,
          variables: [
            createExecutionStartVariable(targetId),
            {
              id: targetId,
              name: variableName || "resultado",
              type: (outputType as VariableType) || frontendType,
              node_id: targetId,
            },
            createExecutionEndVariable(targetId),
          ],
        };
      }

      if (data.type === NodeTypes.DOC_GENERATOR) {
        const outputNameInput = data.inputs.find(
          (i) => i.name === "output_name",
        );
        const outputValue = outputNameInput?.value;
        let parsedName = "documento";

        try {
          if (
            typeof outputValue === "object" &&
            outputValue !== null &&
            "base" in outputValue
          ) {
            parsedName = (outputValue as { base?: string }).base || "documento";
          } else if (typeof outputValue === "string") {
            const parsed = JSON.parse(outputValue);
            parsedName = parsed.base || "documento";
          }
        } catch {
          parsedName = "documento";
        }

        return {
          name: targetName,
          nodeId: targetId,
          type: data.type,
          ref_name: data.type,
          variables: [
            createExecutionStartVariable(targetId),
            {
              id: targetId,
              name: parsedName,
              type: "file" as VariableType,
              node_id: targetId,
            },
            createExecutionEndVariable(targetId),
          ],
        };
      }

      if (data.type === NodeTypes.PAYMENT) {
        return {
          name: targetName,
          nodeId: targetId,
          type: data.type,
          ref_name: data.type,
          variables: [
            createExecutionStartVariable(targetId),
            {
              id: targetId,
              name: "resultado_pago",
              type: "boolean" as VariableType,
              node_id: targetId,
            },
            createExecutionEndVariable(targetId),
          ],
        };
      }

      if (data.type === NodeTypes.INTEROPERABILITY) {
        try {
          const config = deserializeInteroperabilityNode(data, undefined);
          if (config?.serviceId && config.endpointId) {
            const response = await axios.get(`/admin/v1/interoperability`);
            const servicesData = response.data.data || {};
            const service = servicesData[config.serviceId];

            if (service && service[config.endpointId]) {
              const endpoint = service[config.endpointId];
              const responseData = endpoint.responses?.["200"]?.data;

              if (responseData) {
                const outputVariables = Object.entries(
                  responseData as unknown as InteroperabilityResponseData,
                ).map(([key, value]) => {
                  let normalizedType: VariableType = "string";
                  const typeStr = (value.type || "string").toLowerCase();

                  if (typeStr === "integer" || typeStr === "number")
                    normalizedType = "number";
                  else if (typeStr === "boolean") normalizedType = "boolean";
                  else if (typeStr === "date" || typeStr === "datetime")
                    normalizedType = "date";
                  else if (typeStr === "array") normalizedType = "array";

                  return {
                    id: `${targetId}-${key}`,
                    name: value.display_name || key,
                    type: normalizedType,
                    node_id: targetId,
                    output_name: key,
                    ref_name: data.type,
                  };
                });

                if (outputVariables.length > 0) {
                  return {
                    name: targetName,
                    nodeId: targetId,
                    type: data.type,
                    ref_name: data.type,
                    variables: outputVariables,
                  };
                }
              }
            }
          }
        } catch (e) {
          console.error("Error fetching interoperability variables", e);
        }
      }

      if (data.type === NodeTypes.N8N) {
        try {
          const n8nFlowId = data.inputs.find((i) => i.name === "workflow")
            ?.value as string;
          const response = await getN8NFlowSchema(n8nFlowId);
          const outputs = (
            (response?.outputs || []) as unknown as N8NOutputs
          ).flatMap((output) =>
            Object.keys(output).map((key) => ({
              name: key,
              type: output[key].type,
            })),
          );

          return {
            name: targetName,
            nodeId: targetId,
            type: data.type as NodeVariable["type"],
            ref_name: data.type,
            variables: outputs.map((v) => ({
              id: `${targetId}-${v.name}`,
              name: v.name,
              output_name: v.name,
              type: parseType(v.type) as VariableType,
              node_id: targetId,
            })),
          };
        } catch (e) {
          console.error("Error fetching N8N variables", e);
        }
      }

      if (data.type === NodeTypes.MERGE) {
        const firstEntry = data.inputs?.find((i) => i.type === "MERGE_ENTRY");
        if (!firstEntry) return null;

        const entryValue =
          typeof firstEntry.value === "string"
            ? JSON.parse(firstEntry.value)
            : firstEntry.value;
        const entryNodeId = entryValue?.node_id;

        if (entryNodeId) {
          const entryNode = allNodes.find((node) => node.id === entryNodeId);
          if (entryNode) {
            const sourceVariables = await fetchNodeVariables(
              entryNode,
              targetId,
              targetName,
            );
            if (sourceVariables) {
              return {
                ...sourceVariables,
                name: targetName,
                nodeId: targetId,
                type: data.type,
                ref_name: data.type,
                variables: sourceVariables.variables.map((v) => ({
                  ...v,
                  node_id: targetId,
                  id: v.id.replace(entryNodeId, targetId),
                })),
              };
            }
          }
        }
      }

      return null;
    };

    const parseVariables = async () => {
      newVariables.push({
        name: "Variables globales",
        nodeId: "global-variables",
        type: "global-variables",
        variables: [
          {
            id: "CURRENT_TIME",
            global_type: "CURRENT_TIME",
            name: "hora-actual",
            node_id: "global-variables",
            type: "date",
          },
          {
            id: "name",
            global_type: "APPLICANT",
            name: "nombre-tramitador",
            node_id: "global-variables",
            type: "string",
          },
          {
            id: "email",
            global_type: "APPLICANT",
            name: "email-tramitador",
            node_id: "global-variables",
            type: "string",
          },
        ],
      });

      for (const n of notEmptyNodes) {
        const nodeVars = await fetchNodeVariables(n);
        if (nodeVars) {
          newVariables.push(nodeVars);
        }
      }

      setVariables(newVariables);
      setIsLoading(false);
    };
    parseVariables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, allNodes]);

  return {
    isLoading,
    variables,
  };
};

// const getPortalTableColumns = async (tableId: string, source: string) => {
//   try {
//     const response = await axios.get(
//       `/api/admin/v1/data-center/tables/${tableId}`,
//       {
//         params: { source },
//       },
//     );
//     return response.data;
//   } catch (error) {
//     console.error("Error fetching portal table columns:", error);
//     throw error;
//   }
// };

const getTable = async (name: string, source: DataCenterSource) => {
  if (source == "sdt") {
    try {
      const response = await axios.get("/admin/v1/data-center/tables/data", {
        params: { table_name: name },
      });
      return response.data;
    } catch (error) {
      console.error("Error al obtener la tabla:", error);
      throw error;
    }
  } else {
    try {
      const response = await axios.get(
        `/admin/v1/data-center/sources/${source.toUpperCase()}/tables/data`,
        {
          params: { table_name: name },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error al obtener la tabla:", error);
      throw error;
    }
  }
};

type TableColumn = { id: string; name: string; type: string };
type TableInfo = { name: string; columns: TableColumn[] };
type TablesByNode = {
  nodeId: string;
  nodeName: string;
  nodeType: (typeof NodeTypes)[keyof typeof NodeTypes];
  source: string | null;
  tables: TableInfo[];
};

export const useGetVariablesDataCenter = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [variables, setVariables] = useState<NodeVariable[]>([]);
  // ahora tables es: nodo > tablas > columnas
  const [tables, setTables] = useState<TablesByNode[]>([]);

  const { selectedNodeId } = useUIStore();
  const { getNodesVariabes } = useWorkflowStore();
  const nodes = getNodesVariabes(selectedNodeId!);

  useEffect(() => {
    setIsLoading(true);

    const newVariables: NodeVariable[] = [];
    const newTablesByNode: TablesByNode[] = [];

    const notEmptyNodes = nodes.filter((n) => {
      const data = n.data as unknown as NodeData;
      return data.inputs.length > 0;
    });

    const inferType = (value: unknown): string => {
      if (typeof value === "boolean") return "boolean";
      if (typeof value === "number") return "number";
      if (typeof value === "string" && !isNaN(Date.parse(value))) return "date";
      return "string";
    };

    const parseVariables = async () => {
      for (const n of notEmptyNodes) {
        const data = n.data as unknown as NodeData;

        if (data.type === NodeTypes.DATA_CENTER) {
          const tableName = data.inputs.find((input) => input.name === "table")
            ?.value as string;
          if (!tableName) continue;
          const tableSource = getTableSource(data);
          const response = await getTable(tableName, tableSource);
          const DC = response.data as Array<Record<string, unknown>>;
          const action = getNodeActionString(data);
          if (action === "read") {
            let columns: TableColumn[] = [];
            if (Array.isArray(DC) && DC.length > 0) {
              const sampleRow = DC[0];
              columns = Object.keys(sampleRow).map((key) => ({
                id: key,
                name: key,
                type: inferType(sampleRow[key as keyof typeof sampleRow]),
              }));
            }

            newVariables.push({
              name: data.displayName,
              nodeId: n.id,
              type: data.type,
              ref_name: data.type, // data.type contains the ref_name
              source: tableSource,
              variables: columns.map((c) => ({
                id: c.id,
                name: c.name,
                type: c.type as VariableType,
              })),
            });

            newTablesByNode.push({
              nodeId: n.id,
              nodeName: data.displayName,
              nodeType: data.type,
              source: tableSource,
              tables: [
                {
                  name: tableName,
                  columns,
                },
              ],
            });
          }
        }
      }

      setVariables(newVariables);
      setTables(newTablesByNode);
      setIsLoading(false);
    };

    parseVariables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isLoading,
    variables,
    tables, // ahora: TablesByNode[] => nodo > tablas > columnas
  };
};
